<template>
  <div
    class="plus-tv-banner"
    :class="heroRowAlign ? 'plus-tv-banner--hero-align' : ''"
  >
    <div
      class="plus-tv-banner__stage relative overflow-hidden rounded-xl"
      @mouseenter="onStageEnter"
      @mouseleave="onStageLeave"
    >
      <div
        v-for="(slide, i) in slides"
        :key="slide.id"
        class="plus-tv-banner__slide absolute inset-0 transition-opacity duration-500"
        :class="i === activeIndex ? 'opacity-100 z-[1]' : 'pointer-events-none opacity-0 z-0'"
      >
        <video
          v-if="slide.videoUrl && i === activeIndex"
          ref="slideVideo"
          class="absolute inset-0 h-full w-full object-cover"
          :class="isHovering ? 'opacity-100' : 'opacity-0'"
          :src="slide.videoUrl"
          :poster="slide.thumb || undefined"
          :muted="muted"
          loop
          playsinline
          webkit-playsinline
          preload="metadata"
        ></video>
        <img
          v-if="slide.thumb"
          :src="slide.thumb"
          alt=""
          class="absolute inset-0 h-full w-full object-cover transition-opacity duration-300"
          :class="isHovering && slide.videoUrl && i === activeIndex ? 'opacity-0' : 'opacity-100'"
        />
        <div
          v-else
          class="absolute inset-0 bg-gradient-to-br from-violet-950/80 via-slate-900 to-slate-950"
        ></div>

        <a
          v-if="slide.linkUrl && i === activeIndex"
          :href="slideHref(slide.linkUrl)"
          class="plus-tv-banner__hit absolute inset-0 z-[3]"
          :target="isHttpLink(slide.linkUrl) ? '_blank' : '_self'"
          rel="noopener noreferrer"
          :aria-label="slide.name || slide.title || '打开链接'"
          @click="onLinkClick(slide, $event)"
        ></a>

        <div
          class="plus-tv-banner__overlay absolute inset-0 z-[4] flex flex-col items-center justify-center px-6 text-center"
          :class="isHovering ? 'plus-tv-banner__overlay--visible' : ''"
        >
          <!-- <h3
            v-if="slide.title"
            class="text-balance text-[1.35rem] font-bold leading-tight text-white sm:text-[1.75rem]"
          >
            {{ slide.title }}
          </h3> -->
          <button
            v-if="slide.ctaText"
            type="button"
            class="plus-tv-banner__cta mt-5"
            @click.stop="openSlide(slide)"
          >
            <span class="plus-tv-banner__cta-line" aria-hidden="true"></span>
            <span>{{ slide.ctaText }}</span>
            <i class="fas fa-arrow-right text-[10px]"></i>
          </button>
        </div>
      </div>

      <button
        v-if="slides.length > 1"
        type="button"
        class="plus-tv-banner__nav plus-tv-banner__nav--prev"
        :class="isHovering ? 'plus-tv-banner__nav--visible' : ''"
        aria-label="上一张"
        @click.stop="prev"
      >
        <i class="fas fa-chevron-left text-xs"></i>
      </button>
      <button
        v-if="slides.length > 1"
        type="button"
        class="plus-tv-banner__nav plus-tv-banner__nav--next"
        :class="isHovering ? 'plus-tv-banner__nav--visible' : ''"
        aria-label="下一张"
        @click.stop="next"
      >
        <i class="fas fa-chevron-right text-xs"></i>
      </button>
    </div>

    <div class="plus-tv-banner__footer">
      <p class="plus-tv-banner__footer-name">{{ currentName }}</p>
      <div v-if="slides.length > 1" class="plus-tv-banner__dots" aria-hidden="true">
        <button
          v-for="(slide, i) in slides"
          :key="'dot-' + slide.id"
          type="button"
          class="plus-tv-banner__dot"
          :class="i === activeIndex ? 'plus-tv-banner__dot--active' : ''"
          :aria-label="`第 ${i + 1} 张`"
          @click="goTo(i)"
        ></button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PlusTvBannerCarousel',
  props: {
    slides: {
      type: Array,
      default: () => []
    },
    heroRowAlign: { type: Boolean, default: false }
  },
  data() {
    return {
      activeIndex: 0,
      isHovering: false,
      muted: true,
      autoplayTimer: null
    }
  },
  computed: {
    currentSlide() {
      return this.slides[this.activeIndex] || null
    },
    currentName() {
      const s = this.currentSlide
      if (!s) return ''
      return s.name || s.title || ''
    }
  },
  watch: {
    slides() {
      this.activeIndex = 0
      this.restartAutoplay()
    },
    activeIndex() {
      this.stopVideo()
      if (this.isHovering) {
        this.$nextTick(() => this.playActiveVideo())
      }
    }
  },
  mounted() {
    this.restartAutoplay()
  },
  beforeDestroy() {
    this.clearAutoplay()
    this.stopVideo()
  },
  methods: {
    getVideoEl() {
      return this.$refs.slideVideo
    },
    restartAutoplay() {
      this.clearAutoplay()
      if (this.slides.length <= 1) return
      this.autoplayTimer = setInterval(() => {
        if (!this.isHovering) this.next()
      }, 5000)
    },
    clearAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer)
        this.autoplayTimer = null
      }
    },
    goTo(i) {
      if (!this.slides.length) return
      this.activeIndex = ((i % this.slides.length) + this.slides.length) % this.slides.length
    },
    prev() {
      this.goTo(this.activeIndex - 1)
    },
    next() {
      this.goTo(this.activeIndex + 1)
    },
    onStageEnter() {
      this.isHovering = true
      this.clearAutoplay()
      this.playActiveVideo()
    },
    onStageLeave() {
      this.isHovering = false
      this.muted = true
      this.stopVideo()
      this.restartAutoplay()
    },
    async playActiveVideo() {
      const slide = this.currentSlide
      if (!slide || !slide.videoUrl) return
      this.muted = false
      await this.$nextTick()
      const v = this.getVideoEl()
      if (!v) return
      v.muted = false
      try {
        await v.play()
      } catch (e) {
        this.muted = true
        v.muted = true
        try {
          await v.play()
        } catch (e2) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[plus-tv-banner] play failed', e2)
          }
        }
      }
    },
    stopVideo() {
      const v = this.getVideoEl()
      if (!v) return
      v.pause()
      try {
        v.currentTime = 0
      } catch (e) {
        /* ignore */
      }
    },
    isHttpLink(url) {
      return /^https?:\/\//i.test(String(url || '').trim())
    },
    slideHref(url) {
      const u = String(url || '').trim()
      if (!u) return '#'
      if (this.isHttpLink(u)) return u
      return u.startsWith('/') ? u : `/${u}`
    },
    onLinkClick(slide, e) {
      const url = slide && slide.linkUrl ? String(slide.linkUrl).trim() : ''
      if (!url) {
        e.preventDefault()
        return
      }
      if (this.isHttpLink(url)) {
        e.preventDefault()
        window.open(url, '_blank', 'noopener,noreferrer')
        return
      }
      if (url.startsWith('/pages')) {
        e.preventDefault()
        if (process.env.NODE_ENV === 'development') {
          console.warn('[plus-tv-banner] 小程序路径无法在 Web 端打开:', url)
        }
        return
      }
      e.preventDefault()
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    openSlide(slide) {
      this.onLinkClick(slide, { preventDefault() {} })
    }
  }
}
</script>

<style scoped>
.plus-tv-banner {
  display: flex;
  flex-direction: column;
  grid-column: span 2;
  min-height: 0;
  min-width: 0;
}

.plus-tv-banner--hero-align {
  height: 100%;
}

.plus-tv-banner__stage {
  aspect-ratio: 16 / 9;
  width: 100%;
  background: #141418;
}

.plus-tv-banner--hero-align .plus-tv-banner__stage {
  flex: 1 1 auto;
  min-height: 0;
  aspect-ratio: unset;
  height: 100%;
}

.plus-tv-banner__hit {
  cursor: pointer;
  background: transparent;
  text-decoration: none;
}

.plus-tv-banner__overlay {
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.15) 0%,
    rgba(0, 0, 0, 0.35) 55%,
    rgba(0, 0, 0, 0.55) 100%
  );
  opacity: 0;
  transition: opacity 0.25s ease;
  pointer-events: none;
}
.plus-tv-banner__overlay--visible {
  opacity: 1;
}
.plus-tv-banner__overlay--visible .plus-tv-banner__cta {
  pointer-events: auto;
}

.plus-tv-banner__cta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: transparent;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.88);
  cursor: pointer;
  transition: color 0.15s ease;
}
.plus-tv-banner__cta:hover {
  color: #fff;
}
.plus-tv-banner__cta-line {
  display: inline-block;
  width: 48px;
  height: 1px;
  background: rgba(255, 255, 255, 0.45);
}

.plus-tv-banner__nav {
  position: absolute;
  top: 50%;
  z-index: 6;
  display: flex;
  height: 36px;
  width: 36px;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.45);
  color: rgba(255, 255, 255, 0.92);
  transform: translateY(-50%);
  opacity: 0;
  pointer-events: none;
  backdrop-filter: blur(6px);
  transition: opacity 0.2s ease, background 0.15s ease;
  cursor: pointer;
}
.plus-tv-banner__nav--visible {
  opacity: 1;
  pointer-events: auto;
}
.plus-tv-banner__nav:hover {
  background: rgba(0, 0, 0, 0.65);
}
.plus-tv-banner__nav--prev {
  left: 10px;
}
.plus-tv-banner__nav--next {
  right: 10px;
}

.plus-tv-banner__footer {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 8px 2px 0;
  box-sizing: border-box;
}

.plus-tv-banner__footer-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 1.35;
  color: #fff;
}

.plus-tv-banner__dots {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
}

.plus-tv-banner__dot {
  height: 6px;
  width: 6px;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.35);
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
}
.plus-tv-banner__dot--active {
  background: #fff;
  transform: scale(1.05);
}
</style>
